import capitalize from "lodash/capitalize";
import React, {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";

import Box from "veneer/Box";
import InitialsAvatar from "veneer/InitialsAvatar";
import Link from "veneer/Link";
import List from "veneer/List";
import ListItemIcon from "veneer/ListItemIcon";
import VeneerNavBar, {
  NavBarClickableListItem,
  NavBarCollapsePanel,
  NavBarHeader,
  NavItemListText,
} from "veneer/NavBar";
import {
  ExpandLessIcon,
  ExpandMoreIcon,
  VerticalMenuIcon,
} from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

import { ENV, SIDEBAR_EXPANDED_WIDTH } from "cortex/ui/src/constant/app";
import { useFeatureFlag } from "cortex/ui/src/hooks";
import { useLaunchDarklyAuthReady } from "cortex/ui/src/integrations/LaunchDarklyAsync";
import { User } from "cortex/ui/src/user/model";

import { AccountManagementPopover } from "./AccountManagementPopover";
import { HELP_CENTER_NAV_ITEMS, getFleetRouteItems } from "./FleetNavItems";
import { ToggleSource, useNavBarContext } from "./NavBarContext";
import { applyPermissionsToRoutes } from "./permissions";
import { getZeusRouteItems } from "./ZeusNavItems";

const getExpandCollapseIcons = () => ({
  collapseIcon: <ExpandLessIcon htmlColor={Colors.blue200} />,
  expandIcon: <ExpandMoreIcon htmlColor={Colors.blue200} />,
});

const matchesRoute = (
  pathname: string,
  route?: string,
  matchingPaths?: ReadonlyArray<string>,
  options?: { exact?: boolean },
): boolean => {
  if (!pathname) return false;

  const patterns = [route, ...(matchingPaths ?? [])].filter(
    (pattern): pattern is string => Boolean(pattern),
  );

  return patterns.some(
    (pattern) =>
      matchPath(pathname, {
        path: pattern,
        exact: options?.exact ?? true,
        sensitive: false,
        strict: false,
      }) != null,
  );
};

const renderLink = (to: string, key: string, content: ReactNode) => {
  const isPath = to.startsWith("/");
  if (isPath) {
    return (
      <RouterLink key={key} to={to}>
        {content}
      </RouterLink>
    );
  }
  return (
    <Link key={key} newTab path={to}>
      {content}
    </Link>
  );
};

export interface NavItemData {
  altLabel?: string;
  children?: NavItemData[];
  endAdornment?: ReactNode;
  hidden?: boolean;
  icon?: ReactNode;
  id: string;
  label: string;
  matchingPaths?: ReadonlyArray<string>;
  route?: string;
}

interface NavBarProps {
  hideSettings?: boolean;
  user: User;
}

export function NavBar({ hideSettings, user }: NavBarProps) {
  const { isLDContextReady } = useLaunchDarklyAuthReady();
  const appName = capitalize(ENV.APP_NAME);
  const { expanded, toggleExpanded } = useNavBarContext();
  const featureFlags = useFeatureFlag();
  const [accountManagerOpen, setAccountManagerOpen] = useState<boolean>(false);
  const [hasBeenPinned, setHasBeenPinned] = useState<boolean>(expanded);
  const [isClosing, setIsClosing] = useState<boolean>();
  const popperAnchorRef = useRef<HTMLDivElement>(null);
  const ssoEnabled = useFeatureFlag("cortex-internal-sso");

  const fleetRoots = useMemo(() => {
    const base = getFleetRouteItems(featureFlags);
    const withPerms = applyPermissionsToRoutes(base, user);
    return withPerms.filter((r) => !r.hidden);
  }, [featureFlags, user]);

  const zeusRoots = useMemo(() => {
    const base = getZeusRouteItems();
    const withPerms = applyPermissionsToRoutes(base, user);
    return withPerms.filter((r) => !r.hidden);
  }, [user]);

  const navigationRoots = appName === "Fleet" ? fleetRoots : zeusRoots;
  const username = user?.name || "";

  const getNavItems = () => {
    return navigationRoots.map((route) => (
      <NavBarItem
        children={route.children}
        expanded={expanded}
        icon={route.icon}
        key={route.id}
        label={route.label}
        matchingPaths={route.matchingPaths}
        route={route.route}
        toggleExpanded={toggleExpanded}
      />
    ));
  };

  useEffect(() => {
    let timeoutId: number;
    if (isClosing) {
      timeoutId = window.setTimeout(() => {
        setIsClosing(false);
      }, 300);
    }
    return () => clearTimeout(timeoutId);
  }, [isClosing]);

  const toggleDrawer = () => {
    if (expanded) {
      setIsClosing(true);
    }
    // This condition is required for when account manager expands the navbar
    // but does not pin it. In this case, we should not toggle the pinned state.
    if (hasBeenPinned === expanded) {
      setHasBeenPinned((previousPinned) => !previousPinned);
    }
    toggleExpanded(ToggleSource.UserInteraction, !expanded);
  };

  const handleAccountManagerClick = (
    event: KeyboardEvent | ReactMouseEvent,
  ) => {
    if ((event as KeyboardEvent).key === "Enter") {
      handleAccountManagerOpen(true);
      return;
    }

    setAccountManagerOpen((previousAccountManagerOpen) => {
      toggleExpanded(
        ToggleSource.UserInteraction,
        !previousAccountManagerOpen || hasBeenPinned,
      );
      return !previousAccountManagerOpen;
    });
  };

  const handleAccountManagerOpen = (open: boolean) => {
    setAccountManagerOpen(open);
    toggleExpanded(ToggleSource.UserInteraction, hasBeenPinned);
  };

  return (
    <>
      <VeneerNavBar
        isClosing={isClosing}
        open={expanded}
        width={SIDEBAR_EXPANDED_WIDTH}
      >
        <NavBarHeader
          expanded={expanded}
          logoText={`Cortex ${appName}`}
          onExpandButtonClick={toggleDrawer}
          variant="blue"
        />
        <List>{isLDContextReady ? getNavItems() : null}</List>
        <List>
          {HELP_CENTER_NAV_ITEMS.map((route) => (
            <NavBarItem
              children={route.children}
              expanded={expanded}
              icon={route.icon}
              key={route.id}
              label={route.label}
              route={route.route}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </List>
        <List>
          <NavBarClickableListItem
            dataTestId="navbar-account"
            onClick={handleAccountManagerClick}
            ref={popperAnchorRef}
          >
            <ListItemIconWithState selected={accountManagerOpen}>
              <InitialsAvatar fullName={username} />
            </ListItemIconWithState>
            <NavItemContent
              endAdornment={<VerticalMenuIcon htmlColor={Colors.blue200} />}
            >
              <Username>{username}</Username>
            </NavItemContent>
          </NavBarClickableListItem>
        </List>
        <AccountManagementPopover
          anchorEl={popperAnchorRef.current}
          hideSettings={ssoEnabled || hideSettings}
          onOpenChange={handleAccountManagerOpen}
          open={accountManagerOpen}
          user={user}
        />
      </VeneerNavBar>
    </>
  );
}

interface NavBarItemProps {
  children?: NavItemData[];
  expanded?: boolean;
  icon?: ReactNode | string;
  id?: string;
  label: string;
  matchingPaths?: ReadonlyArray<string>;
  route?: string;
  toggleExpanded?: (
    source: ToggleSource,
    isOpened?: boolean | undefined,
  ) => void;
}

function NavBarItem({
  children,
  expanded,
  icon,
  id,
  label,
  matchingPaths,
  route,
}: NavBarItemProps) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { expandIcon, collapseIcon } = getExpandCollapseIcons();

  const viewableChildren = children?.filter(
    (child): child is Required<NavItemData> =>
      !child.hidden && child.route !== undefined,
  );

  const childRoute = viewableChildren?.find((child) =>
    matchesRoute(pathname, child.route, child.matchingPaths, { exact: false }),
  )?.route;

  const hasChildMatches =
    viewableChildren?.some((child) =>
      matchesRoute(pathname, child.route, child.matchingPaths, { exact: true }),
    ) ?? false;

  useEffect(() => {
    setOpen(Boolean(hasChildMatches || childRoute));
  }, [children, pathname, hasChildMatches, childRoute]);

  const handleClick = () => setOpen(!open);

  if (!route && (!viewableChildren || viewableChildren.length === 0)) {
    return null;
  }

  if (!viewableChildren || viewableChildren.length === 0) {
    const parentRoute = route as Required<NavItemData>["route"];
    const parentId =
      (id as Required<NavItemData>["id"]) ||
      (label as Required<NavItemData>["label"]);
    const isSelected = matchesRoute(pathname, parentRoute, matchingPaths, {
      exact: true,
    });
    return renderLink(
      parentRoute,
      parentId,
      <NavBarClickableListItem selected={isSelected} tabIndex={-1}>
        <ListItemIconWithState selected={isSelected}>
          {icon}
        </ListItemIconWithState>
        <NavItemListText>{label}</NavItemListText>
      </NavBarClickableListItem>,
    );
  }

  if (viewableChildren.length === 1) {
    const {
      altLabel,
      id: childId,
      endAdornment: childEndAdornment,
      label: childLabel,
      matchingPaths: childMatchingPaths,
      route: childRoute,
    } = viewableChildren[0];
    const isSelected = matchesRoute(pathname, childRoute, childMatchingPaths, {
      exact: true,
    });
    return renderLink(
      childRoute,
      childId,
      <NavBarClickableListItem selected={isSelected} tabIndex={-1}>
        <ListItemIconWithState selected={isSelected}>
          {icon}
        </ListItemIconWithState>
        <NavItemContent endAdornment={childEndAdornment}>
          {altLabel ?? childLabel}
        </NavItemContent>
      </NavBarClickableListItem>,
    );
  }

  const parentSelected =
    (childRoute &&
      matchesRoute(pathname, childRoute, matchingPaths, { exact: false })) ||
    hasChildMatches;

  return (
    <Box>
      <NavBarClickableListItem
        aria-expanded={open}
        hasChildren
        isNavExpanded={expanded}
        isPanelOpen={open}
        onClick={handleClick}
        selected={parentSelected}
      >
        <ListItemIconWithState selected={parentSelected}>
          {icon}
        </ListItemIconWithState>
        <NavItemContent>{label}</NavItemContent>
        {open ? collapseIcon : expandIcon}
      </NavBarClickableListItem>
      <NavBarCollapsePanel expanded={open} isPanelOpen={open}>
        <Box>
          {viewableChildren.map(
            (child) =>
              !child.hidden &&
              renderLink(
                child.route,
                child.id,
                <NavBarClickableListItem
                  isChild
                  isPanelOpen={open}
                  selected={matchesRoute(
                    pathname,
                    child.route,
                    child.matchingPaths,
                    { exact: true },
                  )}
                  tabIndex={-1}
                >
                  <ListItemIcon />
                  <NavItemContent endAdornment={child.endAdornment}>
                    {child.label}
                  </NavItemContent>
                </NavBarClickableListItem>,
              ),
          )}
        </Box>
      </NavBarCollapsePanel>
    </Box>
  );
}

const NavItemContent = ({
  children,
  endAdornment,
}: {
  children: ReactNode;
  endAdornment?: ReactNode;
}) => {
  return (
    <NavItemListText>
      <Box alignItems="center" display="flex" justifyContent="space-between">
        {children}
        {endAdornment}
      </Box>
    </NavItemListText>
  );
};

const Username = styled(Box)({
  textWrap: "wrap",
});

const ListItemIconWithState = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  "&.MuiListItemIcon-root": {
    color: `${selected ? Colors.white : Colors.blue200} !important`,
  },
}));
